/* Expo Warehouse — Anti-spam guard for newsletter / contact / quote forms.
   Five client-side layers (honeypot, time-on-page, disposable-email blocklist,
   Gmail-dot-trick, per-session rate limit) PLUS a server-side proxy layer
   via Cloudflare Turnstile + Cloudflare Worker for forms that previously
   POSTed directly to SharpSpring (which leaked the webhook URL in page source
   and let bots bypass everything client-side).

   Forms that get the client-side guard: anything matching .newsletter-form,
   #ContactForm, #quote-form, or any form with data-anti-spam="true". Honeypot
   inputs use class .anti-spam-honeypot. Failed validations prevent submit.

   Exposes:
     - window.ExpoAntiSpam.validate(form)      — run all 5 client checks
     - window.ExpoAntiSpam.recordSubmission()  — bump the rate-limit counter
     - window.ExpoAntiSpam.proxySubmit(form, formType)
         → validate + invisible Turnstile token + POST to Cloudflare Worker.
         formType is one of 'quote' | 'newsletter' | 'contact'. Returns a
         Promise that resolves with { success: true } on success or rejects
         with an error reason. */

(function () {
  'use strict';

  // ---- Config ----------------------------------------------------------------
  var MIN_TIME_SECONDS = 2;
  var RATE_LIMIT_KEY = 'expo_anti_spam_subs';
  var RATE_LIMIT_MAX = 3;
  var RATE_LIMIT_WINDOW_MS = 30 * 60 * 1000;
  var GMAIL_DOT_THRESHOLD = 3;

  var TURNSTILE_SITEKEY = '0x4AAAAAADWTOQaGYWorjXwE';
  var TURNSTILE_SCRIPT_URL =
    'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var WORKER_URL = 'https://expowhs-forms-proxy.matt-bc8.workers.dev';

  var FORM_LOAD = new WeakMap();

  var DISPOSABLE = new Set([
    'mailinator.com', 'mailinator.net', 'tempmail.com', 'temp-mail.org', 'temp-mail.io',
    'guerrillamail.com', 'guerrillamail.net', 'guerrillamail.org', 'guerrillamail.biz',
    'guerrillamailblock.com', 'sharklasers.com', 'spam4.me', 'grr.la',
    'yopmail.com', 'yopmail.fr', '10minutemail.com', '10minutemail.net',
    '20minutemail.com', '30minutemail.com', 'maildrop.cc', 'trashmail.com',
    'trashmail.net', 'getairmail.com', 'mintemail.com', 'throwawaymail.com',
    'dispostable.com', 'mailnesia.com', 'tempinbox.com', 'fakeinbox.com',
    'spamgourmet.com', 'mt2015.com', 'mt2014.com', 'pokemail.net',
    'tempr.email', 'inboxbear.com', 'mailcatch.com', 'mailnator.com',
    'tempemail.com', 'jetable.org', 'mytrashmail.com', 'getnada.com',
    'nada.email', 'emailondeck.com', 'fakemail.fr', 'fakemail.net',
    'mailtemp.info', 'mailsac.com', 'mailmetrash.com', 'tempmail.us.com',
    'tempmailo.com', 'inboxalias.com', 'tempmailaddress.com', 'tempail.com',
    'tempmailbox.com', 'discardmail.com', 'spambox.us', 'mvrht.com',
    'mailfa.tk', 'wegwerfmail.de', 'wegwerfmail.net', 'trashinbox.com',
    'temporarymail.org', 'tempmail.email', 'inboxkitten.com', 'spamavert.com',
    '10mail.org', 'rcpt.at', 'tmail.ws', 'tmail.io',
    'mohmal.com', 'burnermail.io', 'mailpoof.com', 'emltmp.com',
    'shitmail.org', 'fivemail.de', 'mailtothis.com', 'mailbox52.ga',
    'tempinbox.co.uk', 'spamfree24.org', 'mailcatch.io', 'guerrillamail.info'
  ]);

  // ---- Helpers ---------------------------------------------------------------
  function emailParts(value) {
    if (!value || typeof value !== 'string') return null;
    var at = value.lastIndexOf('@');
    if (at < 0) return null;
    return {
      local: value.slice(0, at).trim(),
      domain: value.slice(at + 1).trim().toLowerCase()
    };
  }

  function ensureErrorBox(form) {
    var box = form.querySelector('.anti-spam-error');
    if (!box) {
      box = document.createElement('div');
      box.className = 'anti-spam-error';
      box.setAttribute('role', 'alert');
      box.style.cssText =
        'color:#b00020;background:#fdecea;border:1px solid #f5c2c0;' +
        'padding:.5rem .75rem;margin-top:.5rem;border-radius:.25rem;' +
        'font-size:.875rem;display:none;';
      form.appendChild(box);
    }
    return box;
  }

  function showError(form, message) {
    var box = ensureErrorBox(form);
    box.textContent = message;
    box.style.display = 'block';
    setTimeout(function () {
      if (box) box.style.display = 'none';
    }, 8000);
  }

  function getRecentSubmissions() {
    try {
      var now = Date.now();
      var raw = sessionStorage.getItem(RATE_LIMIT_KEY);
      var recent = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(recent)) recent = [];
      return recent.filter(function (t) { return typeof t === 'number' && (now - t) < RATE_LIMIT_WINDOW_MS; });
    } catch (e) {
      return [];
    }
  }

  function recordSubmission() {
    try {
      var recent = getRecentSubmissions();
      recent.push(Date.now());
      sessionStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recent));
    } catch (e) {}
  }

  // ---- Client-side validation (5 layers) -------------------------------------
  function validate(form) {
    var honey = form.querySelector('.anti-spam-honeypot');
    if (honey && honey.value && honey.value.trim() !== '') {
      return { ok: false, silent: true, reason: 'honeypot' };
    }
    var loadAt = FORM_LOAD.get(form) || 0;
    if (loadAt > 0) {
      var elapsed = (Date.now() - loadAt) / 1000;
      if (elapsed < MIN_TIME_SECONDS) {
        return { ok: false, silent: true, reason: 'too-fast' };
      }
    }
    var emailInput =
      form.querySelector('input[type="email"]') ||
      form.querySelector('input[name*="[email]"]') ||
      form.querySelector('input[name="email"]');
    if (emailInput) {
      var parts = emailParts(emailInput.value);
      if (parts) {
        if (DISPOSABLE.has(parts.domain)) {
          return {
            ok: false,
            silent: false,
            message:
              'Please use a permanent business or personal email — temporary / disposable addresses are not accepted.'
          };
        }
        if (parts.domain === 'gmail.com' || parts.domain === 'googlemail.com') {
          var dotMatches = parts.local.match(/\./g);
          var dotCount = dotMatches ? dotMatches.length : 0;
          if (dotCount >= GMAIL_DOT_THRESHOLD) {
            return {
              ok: false,
              silent: false,
              message:
                'Please use your standard email format (excessive dots in Gmail addresses are not accepted).'
            };
          }
        }
      }
    }
    var recent = getRecentSubmissions();
    if (recent.length >= RATE_LIMIT_MAX) {
      return {
        ok: false,
        silent: false,
        message:
          'You have submitted multiple requests recently. Please wait a few minutes before submitting again, or call us at +1 (877) 371-3847.'
      };
    }
    return { ok: true };
  }

  function attach(form) {
    if (form.dataset.antiSpamInit === '1') return;
    form.dataset.antiSpamInit = '1';
    FORM_LOAD.set(form, Date.now());

    form.addEventListener(
      'submit',
      function (event) {
        var result = validate(form);
        if (result.ok) {
          recordSubmission();
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        if (result.silent) {
          showError(
            form,
            'Submission could not be processed. Please reload the page and try again.'
          );
          try { console.warn('[anti-spam] blocked: ' + result.reason); } catch (e) {}
        } else if (result.message) {
          showError(form, result.message);
        }
      },
      true
    );
  }

  function init() {
    var selectors = [
      'form.newsletter-form',
      'form#ContactForm',
      'form#quote-form',
      'form[data-anti-spam="true"]'
    ];
    var forms = document.querySelectorAll(selectors.join(','));
    for (var i = 0; i < forms.length; i++) attach(forms[i]);
  }

  // ---- Turnstile lazy loader + token getter ---------------------------------
  var turnstileLoading = null;
  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (turnstileLoading) return turnstileLoading;
    turnstileLoading = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = TURNSTILE_SCRIPT_URL;
      s.async = true;
      s.defer = true;
      s.onload = function () {
        var attempts = 0;
        var iv = setInterval(function () {
          if (window.turnstile) {
            clearInterval(iv);
            resolve(window.turnstile);
          } else if (++attempts > 60) {
            clearInterval(iv);
            reject(new Error('turnstile-not-ready'));
          }
        }, 100);
      };
      s.onerror = function () { reject(new Error('turnstile-script-load-failed')); };
      document.head.appendChild(s);
    });
    return turnstileLoading;
  }

  function getTurnstileToken() {
    return loadTurnstile().then(function (ts) {
      return new Promise(function (resolve, reject) {
        var container = document.createElement('div');
        container.style.cssText = 'position:absolute;top:-9999px;left:-9999px;width:1px;height:1px;overflow:hidden;';
        document.body.appendChild(container);

        var settled = false;
        var widgetId = null;

        var cleanup = function () {
          setTimeout(function () {
            try { if (widgetId) ts.remove(widgetId); } catch (e) {}
            try { container.remove(); } catch (e) {}
          }, 250);
        };

        var timeout = setTimeout(function () {
          if (settled) return;
          settled = true;
          cleanup();
          reject(new Error('turnstile-timeout'));
        }, 15000);

        try {
          widgetId = ts.render(container, {
            sitekey: TURNSTILE_SITEKEY,
            size: 'invisible',
            retry: 'auto',
            'retry-interval': 2000,
            callback: function (token) {
              if (settled) return;
              settled = true;
              clearTimeout(timeout);
              cleanup();
              resolve(token);
            },
            'error-callback': function (code) {
              if (settled) return;
              settled = true;
              clearTimeout(timeout);
              cleanup();
              reject(new Error('turnstile-error:' + (code || 'unknown')));
            },
            'timeout-callback': function () {
              if (settled) return;
              settled = true;
              clearTimeout(timeout);
              cleanup();
              reject(new Error('turnstile-callback-timeout'));
            }
          });
          if (widgetId == null) {
            settled = true;
            clearTimeout(timeout);
            cleanup();
            reject(new Error('turnstile-render-failed'));
            return;
          }
          // Invisible widgets typically auto-execute, but call execute()
          // explicitly to be safe.
          try { ts.execute(widgetId); } catch (e) { /* auto-executed */ }
        } catch (e) {
          if (!settled) {
            settled = true;
            clearTimeout(timeout);
            cleanup();
            reject(e);
          }
        }
      });
    });
  }

  // ---- High-level submit helper: validate + token + POST to Worker ----------
  // proxySubmit(form, formType[, fields])
  //   form     — the <form> element used for client-side validation
  //   formType — 'quote' | 'contact' | 'newsletter' (Worker uses this to pick
  //              the right SharpSpring webhook)
  //   fields   — optional object map { fieldName: value }. If provided, these
  //              fields are sent to the Worker INSTEAD of pulling FormData from
  //              the form. Use this when the form's input names differ from
  //              what SharpSpring expects (e.g. Shopify uses `contact[name]`
  //              but SharpSpring expects `Name`).
  function proxySubmit(form, formType, fields) {
    var check = validate(form);
    if (!check.ok) {
      if (check.message) showError(form, check.message);
      else if (check.silent) {
        showError(form, 'Submission could not be processed. Please reload the page and try again.');
        try { console.warn('[anti-spam] proxySubmit blocked: ' + check.reason); } catch (e) {}
      }
      return Promise.reject(check);
    }
    return getTurnstileToken().then(function (token) {
      var body = new URLSearchParams();
      if (fields && typeof fields === 'object') {
        Object.keys(fields).forEach(function (k) {
          var v = fields[k];
          if (v === null || v === undefined) return;
          body.append(k, String(v));
        });
      } else {
        var fd = new FormData(form);
        fd.forEach(function (v, k) {
          // Strip honeypot + any internal flags before forwarding
          if (!k) return;
          if (k === 'website') return;            // honeypot field name
          if (k === 'g-recaptcha-response') return;
          body.append(k, typeof v === 'string' ? v : String(v));
        });
      }
      body.append('cf-turnstile-response', token);
      // Pass through SharpSpring tracking cookie if present
      if (!body.has('trackingid__sb')) {
        var m = document.cookie.match(/__ss_tk=([^;]+)/);
        if (m) body.append('trackingid__sb', decodeURIComponent(m[1]));
      }

      return fetch(WORKER_URL + '/?form=' + encodeURIComponent(formType), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
        keepalive: true
      }).then(function (resp) {
        if (!resp.ok) throw new Error('proxy-' + resp.status);
        recordSubmission();
        return resp.json().catch(function () { return { success: true }; });
      });
    });
  }

  // ---- Public API ------------------------------------------------------------
  window.ExpoAntiSpam = {
    validate: validate,
    recordSubmission: recordSubmission,
    proxySubmit: proxySubmit
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ---- Footer newsletter submit: route through Cloudflare Worker ------------
  // (Previously POSTed directly to SharpSpring — that URL was visible in page
  // source. Now goes through the Worker which validates Turnstile first.)
  document.addEventListener(
    'submit',
    function (e) {
      var f = e.target;
      if (!f || (f.id || '').indexOf('newsletter-form-footer') !== 0) return;
      // The form's native submit (to Shopify Customers) still fires. We just
      // ALSO want SharpSpring captured via the Worker — fire and forget.
      try {
        proxySubmit(f, 'newsletter').catch(function (err) {
          try { console.warn('[anti-spam] newsletter proxy failed:', err && err.message ? err.message : err); } catch (e2) {}
        });
      } catch (err) {}
    },
    true
  );
})();
